import React, { useContext, useEffect, useRef } from 'react';
const FootnoteContext = React.createContext(null);
/*
 * Bronson Footnote component.
 */
export function Footnote({ children, numbered, backlinkProps, testId, ...otherProps }) {
    const CustomTag = `${numbered ? 'ol' : 'ul'}`;
    useEffect(() => {
        // Track initial footnote origins on mount
        const footnotes = document.querySelectorAll('.js-footnotes-item[id]');
        footnotes.forEach((footnote) => {
            const footnoteId = footnote.getAttribute('id');
            if (!footnoteId)
                return;
            const key = `footnote-origin:#${footnoteId}`;
            if (sessionStorage.getItem(key))
                return;
            const originLinks = document.querySelectorAll(`a[href="#${footnoteId}"]`);
            for (const link of originLinks) {
                const origin = link.closest('[id]');
                const originId = origin?.getAttribute('id');
                if (originId) {
                    sessionStorage.setItem(key, originId);
                    break;
                }
            }
        });
        // Track updated origin on click
        const allLinks = document.querySelectorAll('a[href^="#"]');
        const handlers = [];
        allLinks.forEach((anchor) => {
            const href = anchor.getAttribute('href');
            const footnoteId = href?.slice(1);
            const parentWithId = anchor.closest('[id]');
            const originId = parentWithId?.getAttribute('id');
            if (!footnoteId || !originId)
                return;
            const handler = () => {
                sessionStorage.setItem(`footnote-origin:${href}`, originId);
                const backlink = document.querySelector(`.js-footnotes-item#${footnoteId} .c-footnotes__backlink`);
                if (backlink) {
                    backlink.href = `#${originId}`;
                }
            };
            anchor.addEventListener('click', handler);
            handlers.push([anchor, handler]);
        });
        return () => {
            handlers.forEach(([anchor, handler]) => anchor.removeEventListener('click', handler));
        };
    }, []);
    return (React.createElement(FootnoteContext.Provider, { value: { backlinkProps } },
        React.createElement(CustomTag, { ...otherProps, "data-testid": testId, className: "c-footnotes" }, children)));
}
Footnote.displayName = 'Footnote';
/*
 * Bronson FootnoteItem component (nested).
 */
function FootnoteItem({ bullet, children, id, ...otherProps }) {
    const context = useContext(FootnoteContext);
    const backlinkRef = useRef(null);
    useEffect(() => {
        if (!context?.backlinkProps || !id)
            return;
        const key = `footnote-origin:#${id}`;
        let originId = sessionStorage.getItem(key);
        if (!originId) {
            const firstRef = document.querySelector(`a[href="#${id}"]`);
            const parentWithId = firstRef?.closest('[id]');
            originId = parentWithId?.getAttribute('id') || null;
            if (originId)
                sessionStorage.setItem(key, originId);
        }
        if (originId && backlinkRef.current) {
            backlinkRef.current.href = `#${originId}`;
        }
    }, [context?.backlinkProps, id]);
    const renderBacklink = () => {
        if (!context?.backlinkProps || !id)
            return null;
        const { content, ariaLabel } = context.backlinkProps;
        return (React.createElement("a", { ref: backlinkRef, className: "c-footnotes__backlink", "aria-label": typeof content === 'string' ? undefined : ariaLabel, href: "#" }, content));
    };
    return (React.createElement("li", { ...otherProps, id: id, "data-footnote-bullet": bullet, className: "js-footnotes-item c-footnotes__item" },
        children,
        renderBacklink()));
}
FootnoteItem.displayName = 'Footnote.Item';
Footnote.Item = FootnoteItem;
